import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { ProfileSections } from "../../../features/profile/constants/profile-sections.enum";
import type { IProfileSection } from "../../../features/profile/types/profile-section/profile-section.entity.interface";
import type { Profile } from "../../../types/profile/profile.entity";
import * as profileApi from "./profile.api";
import { profileQueryKeys } from "./profile.query-keys";

export type ProfileSectionsById = Partial<Record<ProfileSections, IProfileSection>>;

export interface IUseProfileQuery {
  profile: Profile | undefined;
  sectionsById: ProfileSectionsById;
  invalidate(): Promise<void>;
  loading: boolean;
  error: Error | undefined;
}

export function useProfileQuery(): IUseProfileQuery {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: profileQueryKeys.current(),
    queryFn: profileApi.getCurrentProfile,
    cacheTime: 0,
  });

  const invalidate = useCallback(
    function invalidate(): Promise<void> {
      return queryClient.invalidateQueries({
        queryKey: profileQueryKeys.current(),
      });
    },
    [queryClient]
  );

  const sectionsById = useMemo<ProfileSectionsById>(
    function buildSectionsById() {
      const result: ProfileSectionsById = {};
      const sections = query.data?.completion?.sections ?? [];
      for (const section of sections) {
        result[section.id] = section;
      }
      return result;
    },
    [query.data?.completion?.sections]
  );

  return {
    profile: query.data,
    sectionsById,
    invalidate,
    loading: query.isFetching,
    error: query.error as Error | undefined,
  };
}
